import React, { useMemo } from 'react';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { Alert } from 'lib/components/Alert';
import {
  ElasticOrderSearchResults,
  ElasticNoticeSearchResults
} from 'lib/types/searchable';
import { ColumnService } from 'lib/services/directory';
import { Product, PRODUCT_TO_NAME } from 'lib/enums/Product';
import { TableLayout } from 'lib/components/TableLayout';
import { Badge } from 'lib/components/Badge';
import { formatPriceWithCents } from 'lib/pricing/ui';
import Card from 'lib/components/Card/Card';
import { useReportParams } from '../context/ReportParamsContext';
import { useReportsLoading } from '../context/LoadingContext';
import { ReportHeader } from '../components/ReportHeader';
import { REPORT_CURRENCY_SYMBOL } from '../constants/csvColumns';
import { fetchCustomerNoticesAndOrdersAggregations } from '../utils/elastic';
import {
  AdvertiserOrgBucket,
  NoticeAdvertiserOrgBucket,
  reportsV2ProcessCustomerOrdersBuckets,
  reportsV2ProcessCustomerNoticesBuckets,
  ReportV2CustomerAggregationCohort
} from './helpers/customerTabHelpers';

type TableRowData = {
  key: string;
  customer: string;
  email: string;
  revenue: number;
  orderCount: number;
  products: string[];
};

export function CustomersTab() {
  const { getOrgIds, getDateRange } = useReportParams();
  const { dateRange, startDate, endDate } = getDateRange();
  const { withLoading } = useReportsLoading();

  const {
    value: fetchedData,
    isLoading,
    error
  } = useAsyncEffect<{
    orders: ElasticOrderSearchResults;
    notices: ElasticNoticeSearchResults;
  } | null>({
    fetchData: async () =>
      withLoading(
        fetchCustomerNoticesAndOrdersAggregations(getOrgIds, getDateRange)
      ),
    initialData: null,
    dependencies: [getOrgIds().join(','), dateRange, startDate, endDate],
    errorConfig: {
      service: ColumnService.ELASTIC,
      message:
        '[COLUMN REPORTS V2] Failed to fetch customer data for Customers tab',
      tags: {
        orgsFilter: getOrgIds().join(', '),
        dateRange,
        startDateFilter: startDate || '',
        endDateFilter: endDate || ''
      }
    }
  });

  const cohorts = useMemo(() => {
    const ordersAggregations = fetchedData?.orders?.aggregations;
    const noticesAggregations = fetchedData?.notices?.aggregations;

    if (!ordersAggregations && !noticesAggregations) {
      return [];
    }

    if (
      !ordersAggregations?.advertiserOrg?.buckets &&
      !noticesAggregations?.advertiserOrg?.buckets
    ) {
      return [];
    }

    let allCohorts: ReportV2CustomerAggregationCohort[] = [];

    // Process orders data
    const advertiserOrgBuckets = ordersAggregations?.advertiserOrg
      ?.buckets as AdvertiserOrgBucket[];

    if (advertiserOrgBuckets) {
      allCohorts = reportsV2ProcessCustomerOrdersBuckets(
        advertiserOrgBuckets,
        allCohorts
      );
    }

    // Process notices data
    const noticeAdvertiserOrgBuckets = noticesAggregations?.advertiserOrg
      ?.buckets as NoticeAdvertiserOrgBucket[];

    if (noticeAdvertiserOrgBuckets) {
      allCohorts = reportsV2ProcessCustomerNoticesBuckets(
        noticeAdvertiserOrgBuckets,
        allCohorts
      );
    }

    return allCohorts;
  }, [fetchedData]);

  const rows = useMemo(() => {
    return cohorts.map(cohort => {
      const totalRevenue = Object.values(cohort.orderTypes).reduce(
        (sum, orderType) => sum + orderType.revenue,
        0
      );

      const totalOrderCount = Object.values(cohort.orderTypes).reduce(
        (sum, orderType) => sum + orderType.doc_count,
        0
      );

      const products = Object.keys(cohort.orderTypes).map(orderType => {
        const product = orderType as Product;
        return PRODUCT_TO_NAME[product]?.plural || orderType;
      });

      // Try to include orgId or filerId in parentheses after the name
      let customerName: string | null = null;
      if (cohort.orgName) {
        customerName = cohort.orgName;
        if (cohort.cohortKey && cohort.cohortKey !== 'none') {
          customerName += ` (${cohort.cohortKey})`;
        }
      } else if (cohort.firstName || cohort.lastName) {
        customerName = `${cohort.firstName || '-'} ${cohort.lastName || '-'}`;
        if (cohort.cohortKey && cohort.cohortKey !== 'none') {
          customerName += ` (${cohort.cohortKey})`;
        }
      }
      if (!customerName) {
        customerName = '-';
      }

      return {
        key: cohort.cohortKey,
        customer: customerName,
        email: cohort.email || 'N/A',
        revenue: totalRevenue,
        orderCount: totalOrderCount,
        selfServeRate: 'N/A', // TODO: Add self-serve rate once we have the helper implemented
        products
      };
    });
  }, [cohorts]);

  if (!fetchedData) {
    return null;
  }

  return (
    <Card>
      <ReportHeader title="Customers" />
      <div className="p-6">
        {error && (
          <Alert status="error" id="customers-tab-error">
            Error fetching customer data: {error.message}
          </Alert>
        )}
        <TableLayout
          columns={['Customer', 'Email', 'Revenue', 'Orders', 'Products']}
          data={rows}
          id="customers-table"
          loading={isLoading}
          header={{
            title: '',
            subtitle: ''
          }}
          filterable={{
            shouldShowTableItem: (item: TableRowData, search: string) =>
              item.customer.toLowerCase().includes(search.toLowerCase()) ||
              item.email.toLowerCase().includes(search.toLowerCase()),
            searchEnabled: true
          }}
          renderRow={(row: TableRowData) => (
            <>
              <td>{row.customer}</td>
              <td>{row.email}</td>
              <td>
                {formatPriceWithCents(REPORT_CURRENCY_SYMBOL, row.revenue)}
              </td>
              <td>{row.orderCount}</td>
              <td>
                {row.products.map(product => (
                  <Badge key={product}>{product}</Badge>
                ))}
              </td>
            </>
          )}
        />
      </div>
    </Card>
  );
}
